<template>
    <div class="casos-view">
        <div class="page-header">
            <h2>🎲 Gestión de Casos</h2>
            <p>Administra los casos generados por el algoritmo</p>
        </div>

        <!-- Sección de sincronización con apps -->
        <div class="algoritmo-info-section">
            <h3>🔄 Sincronización con Apps</h3>
            <p>Sincroniza los casos con las aplicaciones conectadas.</p>
            <div class="sync-buttons d-flex flex-wrap gap-2">
                <button
                    v-for="app in apps"
                    :key="app.name"
                    class="btn btn-primary"
                    @click="syncWithApps(app.name)"
                    :disabled="syncingCasos[app.name]"
                >
                    <span v-if="syncingCasos[app.name]">⏳ Sincronizando...</span>
                    <span v-else>🔄 Sincronizar Ahora: {{ app.name }}</span>
                </button>
            </div>
        </div>

        <!-- Sección de información y actualización del algoritmo -->
        <div class="algoritmo-info-section">
            <h3>⚙️ Algoritmo de Generación</h3>

            <div v-if="loadingGitInfo" class="text-center">⏳ Cargando información...</div>
            <div v-else-if="gitInfo">
                <div v-if="!gitInfo.exists" class="git-warning">⚠️ El repositorio del algoritmo no ha sido clonado todavía.</div>
                <div v-else-if="gitInfo.exists && !gitInfo.jarExists" class="git-warning">⚠️ El algoritmo no está compilado.</div>
                <div v-else class="row g-3 mb-3">
                    <div class="col-12 col-sm-6 col-lg-3">
                        <div class="git-info-item h-100"><strong>📍 Rama:</strong> {{ gitInfo.branch }}</div>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-3">
                        <div class="git-info-item h-100">
                            <strong>🔖 Commit:</strong>
                            <code>{{ gitInfo.commitShort }}</code>
                            <span class="commit-message">{{ gitInfo.commitMessage }}</span>
                        </div>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-3">
                        <div class="git-info-item h-100"><strong>📅 Fecha:</strong> {{ formatGitDate(gitInfo.commitDate) }}</div>
                    </div>
                    <div class="col-12 col-sm-6 col-lg-3">
                        <div class="git-info-item h-100">
                            <strong>🔄 Estado:</strong>
                            <span v-if="gitInfo.hasUpdates" class="status-update-available"> ⚠️ Actualización disponible </span>
                            <span v-else class="status-up-to-date">✅ Actualizado</span>
                        </div>
                    </div>
                </div>

                <button class="btn btn-primary" @click="updateAlgoritmo" :disabled="updatingAlgoritmo">
                    <span v-if="updatingAlgoritmo">⏳ Preparando...</span>
                    <span v-else-if="!gitInfo.exists">📥 Clonar y Compilar Algoritmo</span>
                    <span v-else-if="!gitInfo.jarExists">🔨 Compilar Algoritmo</span>
                    <span v-else-if="gitInfo.hasUpdates">🔄 Actualizar y Compilar Algoritmo</span>
                    <span v-else>🔨 Recompilar Algoritmo</span>
                </button>
            </div>
        </div>

        <!-- Sección de generación de nuevo caso -->
        <div class="generate-section">
            <h3>➕ Generar Nuevo Caso</h3>
            <div class="generate-form">
                <div class="row g-3">
                    <div class="form-group col-12">
                        <label>Seleccionar un conjunto de JSON guardados como Base:</label>
                        <select v-model="selectedSave" class="form-select" :disabled="generatingCaso">
                            <option value="">-- Selecciona un conjunto de JSON guardado --</option>
                            <option v-for="save in saves" :key="save.username + '-' + save.timestamp" :value="save">
                                {{ save.date }} - {{ save.username }} {{ save.isOwner ? "(tuyo)" : "" }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="row g-3">
                    <div class="form-group col-12 col-sm-6 col-lg-3">
                        <label>Nombre del Caso (opcional):</label>
                        <input
                            type="text"
                            v-model.trim="nombreCaso"
                            class="form-input"
                            placeholder="Ej: caso_faro_nocturno"
                            :disabled="generatingCaso"
                            @input="validateCasoName"
                        />
                        <small>Si lo dejas vacío, se usará el nombre por defecto</small>
                        <span v-if="casoNameError" class="error-text">{{ casoNameError }}</span>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-lg-3">
                        <label>Número de Iteraciones:</label>
                        <input
                            type="number"
                            v-model.number="numIteraciones"
                            class="form-input"
                            min="1"
                            max="10"
                            :disabled="generatingCaso"
                        />
                        <small>Número de iteraciones en la generación (1-10)</small>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-lg-3">
                        <label>Número de Jugadores:</label>
                        <input
                            type="number"
                            v-model.number="numJugadores"
                            class="form-input"
                            min="2"
                            max="12"
                            :disabled="generatingCaso"
                        />
                        <small>Jugadores en el caso (2-12)</small>
                    </div>

                    <div class="form-group col-12 col-sm-6 col-lg-3">
                        <label>Dificultad:</label>
                        <select v-model.number="dificultad" class="form-select" :disabled="generatingCaso">
                            <option :value="0" selected>Muy fácil (0)</option>
                            <option :value="1">Fácil (1)</option>
                            <option :value="2">Normal (2)</option>
                            <option :value="3">Difícil (3)</option>
                            <option :value="4">Muy Difícil (4)</option>
                        </select>
                    </div>
                </div>
                <div class="row g-3">
                    <div class="form-group col-12 col-md-6">
                        <label>Escenario:</label>
                        <select v-model.number="escenarioId" class="form-select" :disabled="generatingCaso || loadingEscenarios">
                            <option v-if="loadingEscenarios" value="">⏳ Cargando escenarios...</option>
                            <option v-else-if="escenarios.length === 0" value="">No hay escenarios disponibles</option>
                            <option v-for="escenario in escenarios" :key="escenario.id" :value="escenario.id">
                                {{ escenario.nombre }} (ID: {{ escenario.id }})
                            </option>
                        </select>
                        <small v-if="selectedEscenario">{{ selectedEscenario.descripcion }}</small>
                    </div>

                    <div class="form-group col-12 col-md-6">
                        <label>Colecciones:</label>
                        <div class="border rounded p-2" style="max-height: 180px; overflow-y: auto">
                            <div v-for="coleccion in colecciones" :key="coleccion.coleccionId" class="form-check">
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    :id="'col-' + coleccion.coleccionId"
                                    :value="coleccion.coleccionId"
                                    v-model="coleccionesIds"
                                    :disabled="generatingCaso"
                                />
                                <label class="form-check-label" :for="'col-' + coleccion.coleccionId">
                                    {{ coleccion.nombre }} (ID: {{ coleccion.coleccionId }})
                                </label>
                            </div>
                            <p v-if="colecciones.length === 0" class="text-muted small mb-0">No hay colecciones disponibles</p>
                        </div>
                        <small>Selecciona al menos una colección</small>
                    </div>
                </div>

                <div class="generate-actions d-flex flex-wrap gap-2 align-items-center">
                    <div class="d-flex align-items-center gap-2 flex-wrap">
                        <button
                            class="btn btn-primary btn-large"
                            @click="generateCaso"
                            :disabled="
                                !selectedSave ||
                                generatingCaso ||
                                !gitInfo.jarExists ||
                                !!casoNameError ||
                                coleccionesIds.length === 0
                            "
                        >
                            <span v-if="generatingCaso">⏳ Generando caso...</span>
                            <span v-else>🎲 Generar Caso</span>
                        </button>

                        <div class="d-flex align-items-center gap-2">
                            <input
                                type="number"
                                v-model.number="numCasosToGenerate"
                                class="form-input"
                                min="1"
                                max="50"
                                style="width: 90px"
                                :disabled="generatingCaso"
                                title="Número de casos a generar"
                            />
                            <button
                                class="btn btn-primary btn-large"
                                @click="generateMultipleCasos"
                                :disabled="
                                    !selectedSave ||
                                    generatingCaso ||
                                    !gitInfo.jarExists ||
                                    !!casoNameError ||
                                    numCasosToGenerate <= 0 ||
                                    coleccionesIds.length === 0
                                "
                            >
                                🎲 Generar {{ numCasosToGenerate }} casos
                            </button>
                        </div>
                    </div>

                    <button
                        v-if="generatingCaso"
                        class="btn btn-danger btn-large"
                        @click="cancelGeneration"
                        :disabled="cancellingGeneration"
                    >
                        <span v-if="cancellingGeneration">🛑 Cancelando...</span>
                        <span v-else>🛑 Cancelar Generación</span>
                    </button>
                </div>
            </div>
            <div v-if="generationOutput" class="generation-output">
                <h4>📋 Resultado de la Generación:</h4>
                <pre>{{ generationOutput }}</pre>
                <div v-if="errorLogFilename" class="d-flex justify-content-between align-items-center gap-2 mt-2 flex-wrap">
                    <button class="btn btn-danger" @click="downloadErrorLog" title="Descargar log del error">
                        📝 Descargar Log del Error
                    </button>
                    <button
                        class="btn btn-danger"
                        @click="downloadErrorConverterLog"
                        title="Descargar log del error del convertidor"
                    >
                        📝 Descargar Log del Error del Convertidor
                    </button>
                </div>
            </div>
        </div>

        <!-- Sección de subir caso externo -->
        <div class="upload-section">
            <h3>📤 Subir Caso Externo</h3>
            <div class="upload-form">
                <div class="row g-3">
                    <div class="form-group col-12">
                        <label>Seleccionar archivo JSON del caso:</label>
                        <input
                            type="file"
                            @change="handleFileSelect"
                            accept=".json"
                            class="form-input-file w-100"
                            :disabled="uploadingCaso"
                        />
                        <small>Selecciona un archivo JSON generado externamente</small>
                    </div>
                </div>

                <div v-if="selectedFile" class="file-preview">
                    <div class="file-info">
                        <span>📄 {{ selectedFile.name }}</span>
                        <span>💾 {{ formatSize(selectedFile.size) }}</span>
                    </div>
                    <button class="btn btn-primary" @click="uploadCaso" :disabled="uploadingCaso || !selectedFile">
                        <span v-if="uploadingCaso">⏳ Subiendo...</span>
                        <span v-else>📤 Subir Caso</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Lista de casos existentes -->
        <div class="casos-list-section">
            <div class="section-header d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h3 class="mb-0">📚 Casos Existentes</h3>
                <button class="btn btn-sm btn-secondary" @click="loadCasos">🔄 Recargar</button>
            </div>

            <div v-if="loading" class="text-center">⏳ Cargando casos...</div>
            <div v-else-if="error" class="alert alert-danger">❌ {{ error }}</div>
            <div v-else-if="casos.length === 0" class="empty-state">📭 No hay casos generados todavía</div>
            <div v-else class="casos-grid">
                <div v-for="caso in casos" :key="caso.filename" class="caso-card">
                    <div class="caso-info">
                        <div class="caso-header">
                            <h4>{{ caso.id }}</h4>
                            <span class="caso-date">{{ formatDate(caso.modified) }}</span>
                        </div>
                        <div class="caso-body">
                            <p class="caso-description">{{ caso.nombre }}</p>
                            <div class="caso-meta">
                                <span>👥 {{ caso.num_jugadores }} jugadoras</span>
                                <span>📍 {{ caso.escenario }}</span>
                                <span>🎚️ Dificultad: {{ caso.dificultad }}</span>
                            </div>
                            <div class="caso-meta">
                                <span>📄 {{ caso.filename }}</span>
                                <span>💾 {{ formatSize(caso.size) }}</span>
                                <span
                                    v-if="caso.hasLog"
                                    class="log-indicator"
                                    :title="`Log disponible (${formatSize(caso.logSize)})`"
                                >
                                    📝 Log
                                </span>
                            </div>
                            <div class="caso-metadata">
                                <div class="metadata-row">
                                    <span class="metadata-label">👤 Creado por:</span>
                                    <span class="metadata-value">{{ caso.createdBy }}</span>
                                </div>
                                <div class="metadata-row">
                                    <span class="metadata-label">📅 Fecha creación:</span>
                                    <span class="metadata-value">{{ formatDate(caso.createdAt) }}</span>
                                </div>
                                <div class="metadata-row" v-if="caso.saveTimestamp !== 'N/A'">
                                    <span class="metadata-label">💾 Base de datos:</span>
                                    <span class="metadata-value"
                                        >{{ caso.saveUsername }} - {{ formatSaveTimestamp(caso.saveTimestamp) }}</span
                                    >
                                </div>
                                <div class="metadata-row" v-if="caso.source && caso.source !== 'N/A'">
                                    <span class="metadata-label">🔗 Fuente:</span>
                                    <span class="metadata-value">{{ caso.source }}</span>
                                </div>
                                <div class="metadata-row" v-if="caso.commit && caso.commit !== 'N/A'">
                                    <span class="metadata-label">🔖 Commit:</span>
                                    <code class="metadata-value">{{ caso.commit }}</code>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="caso-actions">
                        <button class="btn btn-sm btn-primary" @click="viewCaso(caso.filename)" title="Ver/Editar JSON">
                            👁️ Ver
                        </button>
                        <div v-for="(app, index) in apps" :key="index" class="jugable-action-group">
                            <div class="form-check form-check-inline jugable-check">
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    :id="`jugable-${caso.filename}-${index}`"
                                    :checked="caso.jugable && caso.jugable.includes(app.name)"
                                    @change="toggleJugable(caso.filename, app.name)"
                                    :title="`Marcar como jugable en ${app.name}`"
                                />
                                <label class="form-check-label" :for="`jugable-${caso.filename}-${index}`">
                                    Jugable en {{ app.name }}
                                </label>
                            </div>
                            <button
                                class="btn btn-sm btn-success"
                                @click="createGame(caso.filename, index)"
                                :disabled="!caso.jugable || !caso.jugable.includes(app.name)"
                                title="Crear partida con este caso"
                            >
                                🎮 Crear Partida ({{ app.name }})
                            </button>
                        </div>
                        <button
                            v-for="(app, index) in apps"
                            :key="'print-' + index"
                            class="btn btn-sm btn-secondary"
                            @click="printCaso(caso.filename, index)"
                            title="Imprimir caso"
                        >
                            🖨️ Imprimir ({{ app.name }})
                        </button>
                        <button class="btn btn-sm btn-warning" @click="openRenameCaso(caso.filename)" title="Renombrar caso">
                            ✏️ Renombrar
                        </button>
                        <button class="btn btn-sm btn-info" @click="downloadCasoApp(caso.filename)" title="Descargar JSON App">
                            ⬇️ JSON APP
                        </button>
                        <button class="btn btn-sm btn-info" @click="downloadCaso(caso.filename)" title="Descargar JSON">
                            ⬇️ JSON RAW
                        </button>
                        <button
                            class="btn btn-sm btn-secondary"
                            @click="downloadLog(caso.filename)"
                            title="Descargar Log"
                            v-if="caso.hasLog"
                        >
                            📝 Log
                        </button>
                        <button
                            class="btn btn-sm btn-secondary"
                            @click="downloadConverterLog(caso.filename)"
                            title="Descargar Log"
                            v-if="caso.hasLog"
                        >
                            📝 Converter Log
                        </button>
                        <button class="btn btn-sm btn-danger" @click="deleteCaso(caso.filename)" title="Eliminar caso">
                            🗑️ Eliminar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal: Ver/Editar JSON -->
    <div v-if="showViewModal" class="modal-overlay" @click.self="closeViewModal">
        <div class="modal-content modal-lg">
            <div class="modal-header">
                <h3>✏️ Editar JSON del Caso</h3>
                <button @click="closeViewModal" class="btn-close">×</button>
            </div>
            <div class="modal-body">
                <div class="form-group">
                    <label>📄 Archivo: {{ viewingFilename }}</label>
                </div>
                <div class="form-group">
                    <label>Contenido JSON:</label>
                    <textarea v-model="viewingContent" class="json-textarea" rows="20" @input="validateJsonContent"></textarea>
                    <span v-if="jsonContentError" class="error-text">JSON inválido: {{ jsonContentError }}</span>
                    <span v-else class="help-text">Se guardará en el servidor y se avisará al backoffice de las apps</span>
                </div>
            </div>
            <div class="modal-footer">
                <button @click="saveCasoContent" class="btn btn-primary" :disabled="savingContent || !!jsonContentError">
                    <span v-if="savingContent">⏳ Guardando...</span>
                    <span v-else>💾 Guardar</span>
                </button>
                <button @click="closeViewModal" class="btn btn-secondary">Cerrar</button>
            </div>
        </div>
    </div>

    <!-- Modal: Renombrar Caso -->
    <div v-if="showRenameModal" class="modal-overlay" @click.self="closeRenameModal">
        <div class="modal-content">
            <div class="modal-header">
                <h3>✏️ Renombrar Caso</h3>
                <button @click="closeRenameModal" class="btn-close">×</button>
            </div>
            <div class="modal-body">
                <div class="form-group">
                    <label>📄 Archivo actual:</label>
                    <input type="text" class="form-input w-100" :value="renamingFilename" disabled />
                </div>
                <div class="form-group">
                    <label>📝 Nuevo nombre de archivo:</label>
                    <div class="filename-input">
                        <input type="text" class="form-input" v-model="newFilename" placeholder="caso_nuevo_nombre" @input="validateFilename" />
                        <span class="filename-extension">.json</span>
                    </div>
                    <span v-if="filenameError" class="error-text">{{ filenameError }}</span>
                </div>
                <div class="form-group">
                    <label>🏷️ Nombre descriptivo (opcional):</label>
                    <input type="text" class="form-input w-100" v-model="newCasoName" placeholder="Mi caso personalizado" @input="markCasoNameEdited" />
                    <span class="help-text">Este será el nombre visible en la app (nombre.es)</span>
                </div>
            </div>
            <div class="modal-footer">
                <button @click="renameCaso" class="btn btn-primary" :disabled="!!filenameError || !newFilename">
                    💾 Renombrar
                </button>
                <button @click="closeRenameModal" class="btn btn-secondary">Cancelar</button>
            </div>
        </div>
    </div>

    <!-- Modal: Información con la sincronización -->
    <div v-if="showSyncInfoModal" class="modal-overlay" @click.self="closeSyncInfoModal">
        <div class="modal-content modal-lg">
            <div class="modal-header">
                <h3>🔄 Información de Sincronización</h3>
                <button @click="closeSyncInfoModal" class="btn-close">×</button>
            </div>
            <div class="modal-body" v-if="syncCasosResult?.success == true">
                <div class="form-group">
                    <label>Éxito: {{ syncCasosResult.success }}</label>
                </div>
                <div class="form-group">
                    <label>Casos sincronizados: {{ syncCasosResult.synced }}</label>
                </div>
                <div class="form-group">
                    <label>Casos insertados: {{ syncCasosResult.added }}</label>
                </div>
                <div class="form-group">
                    <label>Casos eliminados: {{ syncCasosResult.deleted }}</label>
                </div>
                <div class="form-group">
                    <label>Juegos eliminados: {{ syncCasosResult.gamesDeleted }}</label>
                </div>
                <div class="form-group">
                    <label>Errores:</label>
                    <ul>
                        <li v-for="error in syncCasosResult.errors" :key="error">{{ error }}</li>
                    </ul>
                </div>
            </div>
            <div class="modal-body" v-else>
                <p>Ha habido un error en la sincronización.</p>
                <p>{{ syncCasosError }}</p>
            </div>
            <div class="modal-footer">
                <button @click="closeSyncInfoModal" class="btn btn-secondary">Cerrar</button>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, onMounted, watch, computed} from "vue";
import api from "../services/api";
import {useEscenarios} from "../composables/useGameData.js";
import {useColecciones} from "../composables/useGameData.js";
import {useCasosSync} from "../composables/useCasosSync.js";

const apps = [
    {
        url: import.meta.env.VITE_APP_PROD_URL,
        name: "app",
    },
    {
        url: import.meta.env.VITE_APP_DEV_URL,
        name: "dev",
    },
];

const casos = ref([]);
const saves = ref([]);
const loading = ref(false);
const error = ref(null);

// Usar composable para escenarios
const {escenariosArray} = useEscenarios();

// Usar composable para colecciones
const {coleccionesArray} = useColecciones();

// Composable para sincronizar casos con envivo
const {syncCasos, syncCasosResult, syncCasosError} = useCasosSync();
const colecciones = coleccionesArray; // Ya es un computed, no wrappear de nuevo
const escenarios = escenariosArray; // Ya es un computed, no wrappear de nuevo
const loadingEscenarios = ref(false);
const casosDirectory = ref("");
const selectedSave = ref("");
const generatingCaso = ref(false);
const cancellingGeneration = ref(false);
const generationController = ref(null);
const generationOutput = ref("");
const gitInfo = ref({
    exists: false,
    branch: "",
    commit: "",
    commitShort: "",
    commitMessage: "",
    commitDate: "",
    hasUpdates: false,
    jarExists: false,
});
const loadingGitInfo = ref(false);
const updatingAlgoritmo = ref(false);

// Parámetros de generación
const numIteraciones = ref(1);
const numJugadores = ref(6);
const dificultad = ref(0);
const escenarioId = ref(0);
const coleccionesIds = ref([]);
const nombreCaso = ref("");
const numCasosToGenerate = ref(1);

// Variables para subir caso externo
const selectedFile = ref(null);
const uploadingCaso = ref(false);
const errorLogFilename = ref(null);

// Modales
const showViewModal = ref(false);
const viewingFilename = ref("");
const viewingContent = ref("");
const savingContent = ref(false);
const jsonContentError = ref("");

const showRenameModal = ref(false);
const renamingFilename = ref("");
const newFilename = ref("");
const newCasoName = ref("");
const filenameError = ref("");
const casoNameError = ref("");
// Bandera para saber si el usuario editó manualmente el nombre descriptivo
const nameEditedByUser = ref(false);

const selectedEscenario = computed(() => {
    return escenarios.value.find(e => e.id === escenarioId.value);
});

// Seleccionar la primera colección por defecto cuando estén disponibles (mínimo una)
watch(
    colecciones,
    nuevas => {
        if (coleccionesIds.value.length === 0 && nuevas.length > 0) {
            coleccionesIds.value = [nuevas[0].coleccionId];
        }
    },
    {immediate: true},
);

// Mantener sincronizado newCasoName cuando cambia newFilename, pero solo si el usuario
// no ha editado manualmente el nombre descriptivo (no es bidireccional)
watch(newFilename, (val, oldVal) => {
    const bare = (val || "").replace(/\.json$/i, "").trim();
    if (!nameEditedByUser.value) {
        newCasoName.value = bare;
    }
});

// Cargar casos al montar
onMounted(async () => {
    await loadCasos();
    await loadSaves();
    await loadGitInfo();
    // Las colecciones se cargan automáticamente desde el composable
    // Los escenarios se cargan automáticamente desde el composable
    // Seleccionar el primer escenario por defecto
    if (escenarios.value.length > 0) {
        escenarioId.value = escenarios.value[0].id;
    }
});

const syncingCasos = ref({});

const showSyncInfoModal = ref(false);

async function syncWithApps(serverName) {
    try {
        await syncCasos(serverName);
    } finally {
        showSyncInfoModal.value = true;
        syncingCasos.value[serverName] = false;
    }
}

function closeSyncInfoModal() {
    showSyncInfoModal.value = false;
}

// Cargar lista de casos
async function loadCasos() {
    loading.value = true;
    error.value = null;

    try {
        const response = await fetch("/api/casos", {
            credentials: "include",
        });

        if (!response.ok) {
            throw new Error("Error al cargar casos");
        }

        const data = await response.json();
        casos.value = data.casos;
        casosDirectory.value = data.directory;
    } catch (err) {
        error.value = err.message;
        console.error("Error cargando casos:", err);
    } finally {
        loading.value = false;
    }
}

// Cargar saves disponibles
async function loadSaves() {
    try {
        const data = await api.getSaves();
        saves.value = data.saves;
    } catch (err) {
        console.error("Error cargando saves:", err);
    }
}

// Generar nuevo caso
async function generateCaso() {
    if (!selectedSave.value) return;

    validateCasoName();
    if (casoNameError.value) {
        alert(`❌ ${casoNameError.value}`);
        return;
    }

    const save = selectedSave.value;

    const nombreFinal = nombreCaso.value ? nombreCaso.value : "(por defecto)";
    if (
        !confirm(
            `¿Generar nuevo caso con los siguientes parámetros?\n\nSave: ${save.date} (${save.username})\nNombre: ${nombreFinal}\nIteraciones: ${numIteraciones.value}\nJugadores: ${numJugadores.value}\nDificultad: ${dificultad.value}\nColecciones: ${coleccionesIds.value.join(", ")}`,
        )
    ) {
        return;
    }

    generatingCaso.value = true;
    const controller = new AbortController();
    generationController.value = controller;
    generationOutput.value = "";
    error.value = null;
    errorLogFilename.value = null;

    try {
        const response = await fetch("/api/casos/generate", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            signal: controller.signal,
            body: JSON.stringify({
                saveTimestamp: save.timestamp,
                username: save.username,
                numIteraciones: numIteraciones.value,
                numJugadores: numJugadores.value,
                dificultad: dificultad.value,
                coleccionesIds: coleccionesIds.value,
                escenarioId: escenarioId.value,
                ...(nombreCaso.value ? {casoName: nombreCaso.value} : {}),
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            if (response.status === 499 || data.cancelled) {
                generationOutput.value = "🛑 Generación cancelada";
                alert("🛑 Generación cancelada");
                return;
            }

            // Guardar el nombre del log si está disponible
            if (data.logFilename) {
                errorLogFilename.value = data.logFilename;
            }
            throw new Error(data.error || "Error al generar caso");
        }

        generationOutput.value = data.output || "Caso generado correctamente";

        // Recargar la lista de casos
        await loadCasos();

        // Limpiar selección
        selectedSave.value = "";
        nombreCaso.value = "";
        casoNameError.value = "";

        alert("✅ Caso generado correctamente");
    } catch (err) {
        if (err.name === "AbortError") {
            generationOutput.value = "🛑 Cancelación solicitada. Generación detenida.";
            return;
        }

        error.value = err.message;
        generationOutput.value = `Error: ${err.message}`;
        console.error("Error generando caso:", err);

        // Recargar la lista de casos
        await loadCasos();

        alert("❌ Error al generar caso: " + err.message);
    } finally {
        generatingCaso.value = false;
        cancellingGeneration.value = false;
        generationController.value = null;
    }
}

// Generar múltiples casos en secuencia
async function generateMultipleCasos() {
    if (!selectedSave.value) return;

    validateCasoName();
    if (casoNameError.value) {
        alert(`❌ ${casoNameError.value}`);
        return;
    }

    const n = Number(numCasosToGenerate.value) || 1;
    if (n <= 0) return;

    const save = selectedSave.value;

    if (
        !confirm(
            `¿Generar ${n} casos con los siguientes parámetros?\n\nSave: ${save.date} (${save.username})\nNombre base: ${nombreCaso.value || "(por defecto)"}\nIteraciones: ${numIteraciones.value}\nJugadores: ${numJugadores.value}\nDificultad: ${dificultad.value}\nEscenario: ${escenarioId.value}\nColecciones: ${coleccionesIds.value.join(", ")}`,
        )
    ) {
        return;
    }

    generatingCaso.value = true;
    const controller = new AbortController();
    generationController.value = controller;
    generationOutput.value = "";
    error.value = null;
    errorLogFilename.value = null;

    try {
        for (let i = 0; i < n; i++) {
            // construir nombre por iteración si el usuario indicó uno
            const casoNameParam = nombreCaso.value ? `${nombreCaso.value}_${i + 1}` : undefined;

            generationOutput.value = `Generando ${i + 1}/${n}...\n` + generationOutput.value;

            try {
                const response = await fetch("/api/casos/generate", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    signal: controller.signal,
                    body: JSON.stringify({
                        saveTimestamp: save.timestamp,
                        username: save.username,
                        numIteraciones: numIteraciones.value,
                        numJugadores: numJugadores.value,
                        dificultad: dificultad.value,
                        coleccionesIds: coleccionesIds.value,
                        ...(casoNameParam ? {casoName: casoNameParam} : {}),
                    }),
                });

                const data = await response.json();

                if (!response.ok) {
                    if (response.status === 499 || data.cancelled) {
                        generationOutput.value = "🛑 Generación cancelada" + "\n" + generationOutput.value;
                        alert("🛑 Generación cancelada");
                        break;
                    }

                    // registrar fallo y continuar
                    if (data.logFilename) {
                        errorLogFilename.value = data.logFilename;
                    }
                    generationOutput.value =
                        `❌ Falló caso ${i + 1}: ${data.error || "Error desconocido"}\n` + generationOutput.value;
                    continue;
                }

                generationOutput.value = (data.output || `Caso ${i + 1} generado correctamente`) + "\n" + generationOutput.value;
            } catch (iterErr) {
                // Si fue una cancelación explícita, propagar para salir
                if (iterErr && iterErr.name === "AbortError") {
                    generationOutput.value = "🛑 Cancelación solicitada. Generación detenida.";
                    throw iterErr;
                }

                console.error("Error en generación iteración:", iterErr);
                generationOutput.value =
                    `❌ Excepción caso ${i + 1}: ${iterErr && iterErr.message ? iterErr.message : iterErr}\n` +
                    generationOutput.value;
                // seguir con la siguiente iteración
                continue;
            }

            // pequeña pausa opcional para no saturar el servidor (breve)
            await new Promise(r => setTimeout(r, 200));
        }

        // Recargar la lista de casos
        await loadCasos();

        // Limpiar selección si se generó correctamente
        selectedSave.value = "";
        nombreCaso.value = "";
        casoNameError.value = "";

        alert("✅ Generación en lote finalizada");
    } catch (err) {
        if (err.name === "AbortError") {
            generationOutput.value = "🛑 Cancelación solicitada. Generación detenida.";
            return;
        }

        error.value = err.message;
        generationOutput.value = `Error: ${err.message}`;
        console.error("Error generando casos en lote:", err);

        // Recargar la lista de casos
        await loadCasos();

        alert("❌ Error al generar casos: " + err.message);
    } finally {
        generatingCaso.value = false;
        cancellingGeneration.value = false;
        generationController.value = null;
    }
}

async function cancelGeneration() {
    if (!generatingCaso.value || cancellingGeneration.value) {
        return;
    }

    if (!confirm("¿Seguro que quieres cancelar la generación del caso en curso?")) {
        return;
    }

    cancellingGeneration.value = true;

    try {
        const response = await fetch("/api/casos/generate/cancel", {
            method: "POST",
            credentials: "include",
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok && response.status !== 404) {
            throw new Error(data.error || "No se pudo cancelar la generación");
        }

        if (generationController.value) {
            generationController.value.abort();
        }

        generationOutput.value = data.message || "🛑 Cancelación solicitada.";
        await loadCasos();
    } catch (err) {
        console.error("Error cancelando generación:", err);
        alert("❌ Error al cancelar generación: " + err.message);
    } finally {
        cancellingGeneration.value = false;
    }
}

function validateCasoName() {
    const value = (nombreCaso.value || "").trim();

    if (!value) {
        casoNameError.value = "";
        return;
    }

    if (value.length > 120) {
        casoNameError.value = "El nombre no puede superar 120 caracteres";
        return;
    }

    if (value.includes("..")) {
        casoNameError.value = 'El nombre no puede contener ".."';
        return;
    }

    if (/[\\/:*?"<>|]/.test(value)) {
        casoNameError.value = 'El nombre contiene caracteres inválidos (\\ / : * ? " < > |)';
        return;
    }

    // reemplazamos todos los espacios por guiones bajos
    nombreCaso.value = value.replace(/\s+/g, "_");

    casoNameError.value = "";
}

// Cargar información de Git
async function loadGitInfo() {
    loadingGitInfo.value = true;
    try {
        const response = await fetch("/api/casos/git-info", {
            credentials: "include",
        });

        if (!response.ok) {
            throw new Error("Error al cargar información de Git");
        }

        gitInfo.value = await response.json();
    } catch (err) {
        console.error("Error cargando info de Git:", err);
        error.value = "No se pudo cargar la información del algoritmo";
    } finally {
        loadingGitInfo.value = false;
    }
}

// Actualizar algoritmo
async function updateAlgoritmo() {
    if (!confirm("¿Actualizar y compilar el algoritmo desde Git?\n\nEsto puede tardar unos minutos.")) {
        return;
    }

    updatingAlgoritmo.value = true;
    error.value = null;

    try {
        const response = await fetch("/api/casos/update-algoritmo", {
            method: "POST",
            credentials: "include",
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || "Error al actualizar algoritmo");
        }

        alert(`✅ ${data.message}`);

        // Recargar información de Git
        await loadGitInfo();
    } catch (err) {
        console.error("Error actualizando algoritmo:", err);
        error.value = err.message;
        alert("❌ Error al actualizar algoritmo: " + err.message);
    } finally {
        updatingAlgoritmo.value = false;
    }
}

// Eliminar caso
async function deleteCaso(filename) {
    if (!confirm(`¿Estás seguro de que quieres eliminar el caso "${filename}"?`)) {
        return;
    }

    try {
        const response = await fetch(`/api/casos/${filename}`, {
            method: "DELETE",
            credentials: "include",
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || "Error al eliminar caso");
        }

        alert("✅ Caso eliminado correctamente");
        await loadCasos();
    } catch (err) {
        error.value = err.message;
        console.error("Error eliminando caso:", err);
        alert("❌ Error al eliminar caso: " + err.message);
    }
}

// Descargar caso
function downloadCasoApp(filename) {
    window.location.href = `/api/casos/${filename}/download`;
}

function downloadCaso(filename) {
    window.location.href = `/api/casos/${filename}/download-raw`;
}

// Descargar log
function downloadLog(filename) {
    window.location.href = `/api/casos/${filename}/download-log`;
}

// Descargar log de error recién generado
function downloadConverterLog(filename) {
    if (filename) {
        window.location.href = `/api/casos/${filename}/download-converter-log`;
    }
}

// Descargar log de error recién generado
function downloadErrorLog() {
    if (errorLogFilename.value) {
        window.location.href = `/api/casos/${errorLogFilename.value}/download-log`;
    }
}

// Descargar log de error recién generado
function downloadErrorConverterLog() {
    if (errorLogFilename.value) {
        window.location.href = `/api/casos/${errorLogFilename.value}/download-converter-log`;
    }
}

// Manejar selección de archivo para subir
function handleFileSelect(event) {
    const file = event.target.files[0];
    if (!file) {
        selectedFile.value = null;
        return;
    }

    if (!file.name.endsWith(".json")) {
        alert("❌ El archivo debe ser un JSON (.json)");
        event.target.value = "";
        selectedFile.value = null;
        return;
    }

    selectedFile.value = file;
}

// Subir caso externo
async function uploadCaso() {
    if (!selectedFile.value) {
        return;
    }

    uploadingCaso.value = true;
    error.value = null;

    try {
        // Leer el contenido del archivo
        const fileContent = await readFileAsText(selectedFile.value);
        const casoData = JSON.parse(fileContent);

        // Enviar al servidor
        const response = await fetch("/api/casos/upload", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
                casoData,
                filename: selectedFile.value.name,
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            if (response.status === 409) {
                // Archivo ya existe
                if (
                    confirm(`Ya existe un caso con el nombre "${data.existingFile}".\n¿Deseas reemplazarlo con un nuevo nombre?`)
                ) {
                    // Generar un nuevo nombre con timestamp
                    const timestamp = new Date().toISOString().replace(/[-:T]/g, "").split(".")[0];
                    const newFilename = selectedFile.value.name.replace(".json", `_${timestamp}.json`);

                    casoData.identificador = newFilename.replace(".json", "");

                    const retryResponse = await fetch("/api/casos/upload", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        credentials: "include",
                        body: JSON.stringify({
                            casoData,
                            filename: newFilename,
                        }),
                    });

                    const retryData = await retryResponse.json();

                    if (!retryResponse.ok) {
                        throw new Error(retryData.error || "Error al subir caso");
                    }

                    alert(`✅ Caso subido correctamente como: ${retryData.filename}`);
                } else {
                    return;
                }
            } else {
                throw new Error(data.error || "Error al subir caso");
            }
        } else {
            alert("✅ Caso subido correctamente");
        }

        // Recargar la lista de casos
        await loadCasos();

        // Limpiar selección
        selectedFile.value = null;
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
            fileInput.value = "";
        }
    } catch (err) {
        console.error("Error subiendo caso:", err);
        if (err instanceof SyntaxError) {
            alert("❌ El archivo no es un JSON válido");
        } else {
            alert("❌ Error al subir caso: " + err.message);
        }
    } finally {
        uploadingCaso.value = false;
    }
}

// Función auxiliar para leer archivo como texto
function readFileAsText(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = e => reject(new Error("Error al leer el archivo"));
        reader.readAsText(file);
    });
}

// Activar/desactivar app en el campo jugable del caso
async function toggleJugable(filename, appName) {
    const caso = casos.value.find(c => c.filename === filename);
    if (!caso) return;

    if (!caso.jugable) caso.jugable = [];

    // Guardar estado anterior para revertir si hay error
    const jugableAnterior = [...caso.jugable];

    const idx = caso.jugable.indexOf(appName);
    if (idx >= 0) {
        caso.jugable.splice(idx, 1);
    } else {
        caso.jugable.push(appName);
    }

    try {
        const response = await fetch(`/api/${appName}/casos/${caso.id}/jugable`, {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            credentials: "include",
            body: JSON.stringify({jugable: caso.jugable}),
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || "Error al actualizar campo jugable");
        }
    } catch (err) {
        // Revertir el cambio optimista
        caso.jugable = jugableAnterior;
        console.error("Error actualizando jugable:", err);
        alert("❌ Error al actualizar campo jugable: " + err.message);
    }
}

// Crear partida con caso
function createGame(filename, urlIdx) {
    // Extraer el casoId del filename (sin extensión .json)
    const casoId = filename.replace(".json", "");
    const url = `${apps[urlIdx].url}/crear?caso=${casoId}`;
    window.open(url, "_blank");
}

// Imprimir caso
function printCaso(filename, urlIdx) {
    // Extraer el casoId del filename (sin extensión .json)
    const casoId = filename.replace(".json", "");
    const url = `${apps[urlIdx].url}/print-caso/${casoId}`;
    window.open(url, "_blank");
}

// Ver JSON del caso
async function viewCaso(filename) {
    try {
        const response = await fetch(`/api/casos/${filename}/view`, {
            credentials: "include",
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || "Error al cargar caso");
        }

        const data = await response.json();
        viewingFilename.value = filename;
        viewingContent.value = JSON.stringify(data.content, null, 2);
        jsonContentError.value = "";
        showViewModal.value = true;
    } catch (err) {
        console.error("Error cargando caso:", err);
        alert("❌ Error al cargar caso: " + err.message);
    }
}

function validateJsonContent() {
    try {
        JSON.parse(viewingContent.value);
        jsonContentError.value = "";
    } catch (err) {
        jsonContentError.value = err.message;
    }
}

// Guardar JSON editado del caso
async function saveCasoContent() {
    let parsedContent;
    try {
        parsedContent = JSON.parse(viewingContent.value);
    } catch (err) {
        jsonContentError.value = err.message;
        return;
    }

    savingContent.value = true;
    try {
        const response = await fetch(`/api/casos/${viewingFilename.value}`, {
            method: "PUT",
            headers: {"Content-Type": "application/json"},
            credentials: "include",
            body: JSON.stringify({content: parsedContent}),
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || "Error al guardar caso");
        }

        // Mostrar el JSON con el formato canonical guardado en el servidor
        viewingContent.value = JSON.stringify(parsedContent, null, 2);

        if (data.errors && data.errors.length > 0) {
            alert(`⚠️ Caso guardado, pero hubo errores al avisar al backoffice:\n${data.errors.join("\n")}`);
        } else {
            alert(`✅ Caso guardado y notificado al backoffice de: ${(data.appsNotified || []).join(", ")}`);
        }

        await loadCasos();
    } catch (err) {
        console.error("Error guardando caso:", err);
        alert("❌ Error al guardar caso: " + err.message);
    } finally {
        savingContent.value = false;
    }
}

function closeViewModal() {
    showViewModal.value = false;
    viewingFilename.value = "";
    viewingContent.value = "";
    savingContent.value = false;
    jsonContentError.value = "";
}

// Renombrar caso
async function openRenameCaso(filename) {
    renamingFilename.value = filename;
    newFilename.value = filename.replace(/\.json$/i, "");
    newCasoName.value = "";
    filenameError.value = "";
    nameEditedByUser.value = false;

    // Cargar el contenido para obtener el nombre actual
    try {
        const response = await fetch(`/api/casos/${filename}/view`, {
            credentials: "include",
        });

        if (response.ok) {
            const data = await response.json();
            // Obtener nombre.es si existe
            if (data.content && data.content.nombre && data.content.nombre.es) {
                newCasoName.value = data.content.nombre.es;
            }
        }
    } catch (err) {
        console.error("Error cargando nombre del caso:", err);
    }

    showRenameModal.value = true;
}

function validateFilename() {
    const filename = (newFilename.value || "").trim();

    if (!filename) {
        filenameError.value = "El nombre no puede estar vacío";
        return;
    }

    if (/\s/.test(filename)) {
        filenameError.value = "El nombre no puede contener espacios";
        return;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(filename)) {
        filenameError.value = "Solo se permiten letras, números, guiones (-) y guion bajo (_)";
        return;
    }

    filenameError.value = "";
}

function markCasoNameEdited() {
    // El usuario ha tocado manualmente el nombre descriptivo; dejar de sincronizar
    nameEditedByUser.value = true;
}

async function renameCaso() {
    if (filenameError.value || !newFilename.value) {
        return;
    }

    const normalizedFilename = `${newFilename.value.trim()}.json`;

    try {
        const response = await fetch(`/api/casos/${renamingFilename.value}/rename`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
                newFilename: normalizedFilename,
                newName: newCasoName.value || undefined,
            }),
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || "Error al renombrar caso");
        }

        alert("✅ Caso renombrado correctamente");
        closeRenameModal();

        await loadCasos();
    } catch (err) {
        console.error("Error renombrando caso:", err);
        alert("❌ Error al renombrar caso: " + err.message);
    }
}

function closeRenameModal() {
    showRenameModal.value = false;
    renamingFilename.value = "";
    newFilename.value = "";
    newCasoName.value = "";
    filenameError.value = "";
    nameEditedByUser.value = false;
}

// Formatear fecha
function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleString("es-ES", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

// Formatear timestamp del save
function formatSaveTimestamp(timestamp) {
    if (!timestamp || timestamp === "N/A") return "N/A";
    // Timestamp formato: 2024-12-17T14-30-45
    const cleaned = timestamp.replace(/T/, " ").replace(/-/g, (match, offset) => {
        // Solo reemplazar los guiones de la hora, no los de la fecha
        const parts = timestamp.split("T");
        if (parts.length === 2 && offset > parts[0].length) {
            return ":";
        }
        return match;
    });
    return cleaned;
}

// Formatear fecha de git
function formatGitDate(dateString) {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("es-ES", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

// Formatear tamaño de archivo
function formatSize(bytes) {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
}
</script>

<style scoped>
.casos-view {
    padding: 20px;
}

.page-header {
    margin-bottom: 30px;
    padding-bottom: 15px;
    border-bottom: 2px solid var(--bs-border-color);
}

.page-header h2 {
    margin: 0 0 10px 0;
    color: var(--bs-body-color);
}

.page-header p {
    margin: 0;
    color: var(--bs-secondary-color);
}

/* Sección de generación */
.generate-section {
    background: var(--bs-body-bg);
    border: 2px solid var(--bs-primary);
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 30px;
}

.generate-section h3 {
    margin: 0 0 15px 0;
    color: var(--bs-primary);
}

.generate-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.form-group label {
    font-weight: 500;
    font-size: 0.95em;
}

.filename-input {
    display: flex;
}

.filename-input input {
    flex: 1;
    min-width: 0;
    border-radius: 4px 0 0 4px;
}

.filename-extension {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    border: 1px solid var(--bs-border-color);
    border-left: 0;
    border-radius: 0 4px 4px 0;
    background: var(--bs-tertiary-bg);
    color: var(--bs-secondary-color);
}

.form-group small {
    color: var(--bs-secondary-color);
    font-size: 0.85em;
    margin-top: 2px;
}

.form-select,
.form-input {
    padding: 8px 12px;
    border: 1px solid var(--bs-border-color);
    border-radius: 4px;
    background: var(--bs-body-bg);
    color: var(--bs-body-color);
    font-size: 1em;
}

.form-input:focus,
.form-select:focus {
    outline: none;
    border-color: var(--bs-primary);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.btn-large {
    padding: 12px 24px;
    font-size: 1.1em;
    font-weight: 600;
}

.generation-output {
    margin-top: 20px;
    padding: 15px;
    background: var(--bs-secondary-bg);
    border-radius: 4px;
}

.generation-output h4 {
    margin: 0 0 10px 0;
    font-size: 1rem;
}

.generation-output pre {
    margin: 0 0 15px 0;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-size: 0.9em;
    color: var(--bs-body-color);
}

/* Sección de subir caso */
.upload-section {
    background: var(--bs-body-bg);
    border: 2px solid var(--bs-success, #198754);
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 30px;
}

.upload-section h3 {
    margin: 0 0 15px 0;
    color: var(--bs-success, #198754);
}

.upload-form {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.form-input-file {
    padding: 8px;
    border: 2px dashed var(--bs-border-color);
    border-radius: 4px;
    background: var(--bs-secondary-bg);
    cursor: pointer;
    transition: all 0.2s;
}

.form-input-file:hover {
    border-color: var(--bs-success, #198754);
    background: var(--bs-body-bg);
}

.file-preview {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px;
    background: var(--bs-secondary-bg);
    border-radius: 6px;
    gap: 15px;
    flex-wrap: wrap;
}

.file-info {
    display: flex;
    gap: 15px;
    align-items: center;
    font-size: 0.95em;
}

.file-info span:first-child {
    font-weight: 600;
    color: var(--bs-body-color);
}

.file-info span:last-child {
    color: var(--bs-secondary-color);
}

/* Lista de casos */
.casos-list-section {
    margin-top: 30px;
}

.empty-state {
    text-align: center;
    padding: 40px;
    color: var(--bs-secondary-color);
    font-size: 1.1em;
}

.casos-grid {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.caso-card {
    background: var(--bs-body-bg);
    border: 1px solid var(--bs-border-color);
    border-radius: 8px;
    padding: 15px;
    transition: all 0.2s;
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.caso-card:hover {
    border-color: var(--bs-primary);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.caso-info {
    min-width: 0;
}

.caso-header {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.caso-header h4 {
    margin: 0;
    font-size: 1.1em;
    color: var(--bs-primary);
    overflow-wrap: anywhere;
}

.caso-date {
    font-size: 0.85em;
    color: var(--bs-secondary-color);
}

.caso-body {
    margin-top: 8px;
}

.caso-description {
    margin: 0 0 10px 0;
    color: var(--bs-body-color);
    font-size: 0.95em;
}

.caso-meta {
    display: flex;
    gap: 4px 15px;
    font-size: 0.85em;
    color: var(--bs-secondary-color);
    flex-wrap: wrap;
    align-items: center;
    word-break: break-word;
}

.log-indicator {
    background: var(--bs-info-bg-subtle, #cfe2ff);
    color: var(--bs-info-text, #084298);
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 600;
    cursor: help;
}

.caso-metadata {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba(102, 126, 234, 0.2);
    font-size: 0.85em;
}

.metadata-row {
    display: flex;
    gap: 8px;
    margin-bottom: 4px;
    flex-wrap: wrap;
}

.metadata-label {
    font-weight: 600;
    color: var(--bs-secondary-color);
}

.metadata-value {
    color: var(--bs-body-color);
    min-width: 0;
    word-break: break-word;
}

.caso-actions {
    display: flex;
    flex-direction: row;
    gap: 10px;
    flex-wrap: wrap;
}

.caso-actions button {
    white-space: nowrap;
}

.jugable-action-group {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
}

.jugable-check {
    font-size: 0.85em;
    color: var(--bs-secondary-color);
}

/* Info footer */
.info-footer {
    margin-top: 30px;
    padding: 15px;
    background: var(--bs-secondary-bg);
    border-radius: 4px;
    font-size: 0.9em;
    color: var(--bs-secondary-color);
}

.info-footer code {
    background: var(--bs-body-bg);
    padding: 2px 6px;
    border-radius: 3px;
    color: var(--bs-body-color);
}

/* Algoritmo info section */
.algoritmo-info-section {
    background: var(--bs-body-bg);
    border: 1px solid var(--bs-border-color);
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 25px;
}

.algoritmo-info-section h3 {
    margin-top: 0;
    margin-bottom: 15px;
    font-size: 1.2em;
    color: var(--bs-body-color);
}

.git-info-item {
    background: var(--bs-secondary-bg);
    padding: 12px;
    border-radius: 6px;
}

.git-info-item label {
    display: block;
    font-size: 0.85em;
    color: var(--bs-secondary-color);
    margin-bottom: 5px;
    font-weight: 500;
}

.git-info-item .value {
    font-size: 0.95em;
    color: var(--bs-body-color);
    word-break: break-all;
}

.git-info-item code {
    background: var(--bs-body-bg);
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 0.9em;
    color: var(--bs-primary);
}

.git-warning {
    background: var(--bs-warning-bg-subtle, #fff3cd);
    color: var(--bs-warning-text, #997404);
    border: 1px solid var(--bs-warning-border-subtle, #ffecb5);
    padding: 12px;
    border-radius: 6px;
    margin-bottom: 15px;
}

.status-update-available {
    color: var(--bs-warning, #ffc107);
    font-weight: 600;
}

.status-up-to-date {
    color: var(--bs-success, #198754);
    font-weight: 600;
}

/* Botones */
.btn {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-primary {
    background: var(--bs-primary);
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background: var(--bs-primary-dark, #5a67d8);
}

.btn-secondary {
    background: var(--bs-secondary);
    color: white;
}

.btn-success {
    background: var(--bs-success, #198754);
    color: white;
}

.btn-info {
    background: var(--bs-info);
    color: white;
}

.btn-danger {
    background: var(--bs-danger);
    color: white;
}

.btn-sm {
    padding: 6px 12px;
    font-size: 0.9em;
}

.alert {
    padding: 12px 16px;
    border-radius: 4px;
    margin-bottom: 20px;
}

.alert-danger {
    background: var(--bs-danger-bg-subtle, #f8d7da);
    color: var(--bs-danger-text, #842029);
    border: 1px solid var(--bs-danger-border-subtle, #f5c2c7);
}

.text-center {
    text-align: center;
    padding: 40px;
    color: var(--bs-secondary-color);
}

/* Modo oscuro */
[data-bs-theme="dark"] {
    --bs-primary-dark: #4c5fd1;
    --bs-secondary: #6c757d;
    --bs-info: #0dcaf0;
    --bs-danger: #dc3545;
    --bs-danger-bg-subtle: #2c0b0e;
    --bs-danger-text: #ea868f;
    --bs-danger-border-subtle: #842029;
}

/* Modales */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    overflow-y: auto;
}

.modal-lg {
    max-width: 900px;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid var(--bs-border-color, #dee2e6);
    flex-shrink: 0;
}

.modal-header h3 {
    margin: 0;
}

.modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
}

.modal-footer {
    padding: 15px 20px;
    border-top: 1px solid var(--bs-border-color, #dee2e6);
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    flex-wrap: wrap;
    flex-shrink: 0;
}

.btn-close {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    color: var(--bs-secondary-color, #666);
}

.btn-close:hover {
    color: var(--bs-body-color, #000);
}

.json-textarea {
    width: 100%;
    font-family: "Courier New", monospace;
    font-size: 0.85em;
    padding: 12px;
    border: 1px solid var(--bs-border-color, #dee2e6);
    border-radius: 4px;
    background: var(--bs-body-bg, #f8f9fa);
    color: var(--bs-body-color, #000);
    resize: vertical;
    min-height: 400px;
}

.error-text {
    color: var(--bs-danger, #dc3545);
    font-size: 0.85em;
    display: block;
    margin-top: 4px;
}

.help-text {
    color: var(--bs-secondary-color, #6c757d);
    font-size: 0.85em;
    display: block;
    margin-top: 4px;
}

[data-bs-theme="dark"] .json-textarea {
    background: #1a1a1a;
    color: #e0e0e0;
    border-color: #444;
}

/* Responsive: tablets y móviles */
@media (max-width: 768px) {
    .modal-overlay {
        padding: 8px;
    }

    /* Neutraliza el margin del estilo global que, junto con width: 100%, desborda en móvil */
    .modal-content {
        margin: 0;
    }

    .modal-header,
    .modal-body,
    .modal-footer {
        padding: 12px;
    }

    .json-textarea {
        min-height: 220px;
    }
}

@media (max-width: 576px) {
    .casos-view {
        padding: 10px 0;
    }

    .generate-section,
    .upload-section,
    .algoritmo-info-section {
        padding: 12px;
    }

    .sync-buttons .btn {
        width: 100%;
    }

    .generate-actions > * {
        width: 100%;
    }

    .generate-actions .btn-large {
        flex: 1 1 auto;
    }

    .file-preview {
        flex-direction: column;
        align-items: stretch;
    }

    .file-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
    }

    .caso-card {
        padding: 12px;
    }

    .caso-actions {
        flex-direction: column;
    }

    .caso-actions button,
    .jugable-action-group {
        width: 100%;
    }

    .empty-state,
    .text-center {
        padding: 20px;
    }
}
</style>
